// Libraries
import { useEffect, useRef, useState } from "react";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

// @mui material components
import Grid from "@mui/material/Grid2";
import Card from "@mui/material/Card";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";

// Redux Actions
// import * as Yup from "yup";
import { addBanner, clearErrors } from "store/actions/bannerActions";
import setAlert from "store/actions/alertActions";
import { scrollToTop, useHandleError } from "components/helper";

// Layouts
import DashboardLayout from "layouts/DashboardLayout";

// Custom Components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardNavbar from "components/DashboardNavbar";
import Footer from "components/Footer";

// Schema
import validations from "./schemas/validations";
import form from "./schemas/form";
import initialValues from "./schemas/initialValues";
import Information from "./components/Information";
import Review from "./components/Review";

function getSteps() {
  return ["Information", "Review"];
}

function getStepContent(
  stepIndex,
  formData,
  selectedPosition,
  handlePositionChange,
  selectedDestination,
  handleDestinationChange
) {
  switch (stepIndex) {
    case 0:
      return (
        <Information
          formData={formData}
          selectedPosition={selectedPosition}
          handlePositionChange={handlePositionChange}
          selectedDestination={selectedDestination}
          handleDestinationChange={handleDestinationChange}
        />
      );
    case 1:
      return <Review formData={formData} />;
    default:
      return null;
  }
}

const NewBanner = () => {
  const formRef = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // selector
  const error = useSelector((state) => state.banners.error);

  // state
  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedDestination, setSelectedDestination] = useState(null);

  const { formId, formField } = form;

  const isLastStep = activeStep === steps.length - 1;

  const sleep = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  const handleBack = () => {
    setActiveStep(activeStep - 1);
    scrollToTop(); // Scroll to the top
  };

  const submitForm = async (values, actions) => {
    await sleep(1000);
    const updatedValues = { ...values };

    if (updatedValues && updatedValues?.image) {
      delete updatedValues?.image;
    }
    if (updatedValues && updatedValues?.location && updatedValues?.location?._id) {
      updatedValues.location = updatedValues?.location?._id;
    }

    // eslint-disable-next-line no-alert
    dispatch(addBanner(updatedValues, values?.image, values?.mobileImage));
    actions.setSubmitting(false);
    actions.resetForm();

    setActiveStep(0);
  };

  const handleSubmit = (values, actions) => {
    if (isLastStep) {
      submitForm(values, actions);
      navigate(`/admin/banners`);
    } else {
      setActiveStep(activeStep + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
      scrollToTop(); // Scroll to the top
    }
  };

  const handlePositionChange = (value) => {
    if (value?.value !== "leaderboard") {
      formRef.current.setFieldValue("destination", null);
      formRef.current.setFieldValue("url", null);
      setSelectedDestination(null);
    }
    setSelectedPosition(value || {});
  };

  // useEffect
  useEffect(() => {
    setSteps(getSteps("admin"));
  }, []);

  useHandleError(error, dispatch, setAlert, clearErrors); // Use the custom hook
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} mb={20}>
        <Grid container justifyContent="center" alignItems="center" sx={{ height: "100%", mt: 8 }}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <Formik
              initialValues={initialValues}
              validationSchema={
                steps[activeStep] === "Review" ? undefined : validations[activeStep]
              }
              onSubmit={handleSubmit}
              innerRef={(f) => {
                formRef.current = f;
              }}
            >
              {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                <Form id={formId} autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <MDBox mx={2} mt={-3}>
                      <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((label) => (
                          <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                          </Step>
                        ))}
                      </Stepper>
                    </MDBox>
                    <MDBox p={3}>
                      <MDBox>
                        {getStepContent(
                          activeStep,
                          {
                            values,
                            touched,
                            formField,
                            errors,
                            setFieldValue,
                          },
                          selectedPosition,
                          handlePositionChange,
                          selectedDestination,
                          setSelectedDestination
                        )}
                        <MDBox mt={2} width="100%" display="flex" justifyContent="space-between">
                          {activeStep === 0 ? (
                            <MDBox />
                          ) : (
                            <MDButton variant="gradient" color="light" onClick={handleBack}>
                              back
                            </MDButton>
                          )}
                          <div>
                            <MDButton
                              onClick={() => navigate("/admin/banners")}
                              rel="noreferrer"
                              variant="outlined"
                              color="dark"
                              sx={{ marginRight: "8px" }}
                            >
                              Cancel
                            </MDButton>
                            <MDButton
                              disabled={isSubmitting}
                              type="submit"
                              variant="gradient"
                              color="dark"
                            >
                              {isLastStep ? "add" : "next"}
                            </MDButton>
                          </div>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default NewBanner;
